import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, HandCoins } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContributionDetailDialog } from '@/components/giving/contribution-detail-dialog';
import { VoidReasonDialog } from '@/components/giving/void-reason-dialog';
import { RecordContributionSaveOptions } from '@/components/giving/record-contribution-save-options';
import {
  AmountRangeFilter,
  amountFilterBounds,
  emptyAmountFilter,
  isAmountFilterApplied,
  type AmountFilter,
} from '@/components/shared/amount-range-filter';
import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import {
  DateRangeFilter,
  INSIGHTS_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FilterBar } from '@/components/shared/filter-bar';
import { FundSelect } from '@/components/shared/fund-select';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { GivingTrendChart } from '@/components/giving/giving-trend-chart';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useGivingSettings } from '@/hooks/use-giving-settings';
import { api } from '@/lib/api';
import {
  dataTableMoneyColumnTotals,
  formatInsightsMoneySummary,
  insightsHasMultipleCurrencies,
} from '@/lib/money-totals';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { resolveDefaultFund } from '@/lib/giving-default-funds';
import {
  loadRecordContributionPreferences,
  persistRecordContributionPreferences,
} from '@/lib/record-contribution-preferences';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { ContributionAccountField } from '@/components/giving/contribution-account-field';
import {
  accountKindForMethod,
  CONTRIBUTION_METHODS,
  type ContributionMethod,
} from '@/lib/contribution-methods';
import { formatDate, formatMoney, formatNumber, formatPercent } from '@/utils/format';

import { GivingSubnav } from './giving-subnav';

interface TitheInsights {
  amountMinor: number;
  count: number;
  currency: string;
  byCurrency?: Array<{ currency: string; amountMinor: number; count: number }>;
  comparison: {
    change: number;
    previousAmountMinor: number;
    previousCount: number;
  };
  byFund: Array<{ fundId: string; fundName: string; amountMinor: number; currency: string }>;
  trend: Array<{
    key: string;
    label: string;
    amountMinor: number;
    count: number;
    grain?: 'day' | 'week' | 'month';
  }>;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

interface Contribution {
  id: string;
  fundId: string;
  memberId: string | null;
  giverName: string | null;
  identityHidden: boolean;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  isOnline: boolean;
  receiptNumber: number;
  isVoided: boolean;
  voidReason: string | null;
  voidedByName: string | null;
  canVoid: boolean;
}

const VOIDED_INCLUDE = 'include';
const VOIDED_HIDE = 'hide';
const SOURCE_ANY = '__any_source__';
const SOURCE_ONLINE = 'online';
const SOURCE_MANUAL = 'manual';

const todayIso = () => new Date().toISOString().slice(0, 10);

export const TithesPage = () => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const timezone = usePreferences((state) => state.timezone);

  const [open, setOpen] = useState(false);
  const [detailContributionId, setDetailContributionId] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<Contribution | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidPassword, setVoidPassword] = useState('');
  const [reasonTarget, setReasonTarget] = useState<Contribution | null>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [fundCurrency, setFundCurrency] = useState<string | null>(null);
  const [formMemberId, setFormMemberId] = useState<string | null>(null);
  const [formMemberQuery, setFormMemberQuery] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [contributionDate, setContributionDate] = useState<string | null>(todayIso());
  const [method, setMethod] = useState<ContributionMethod>('cash');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterMemberId, setFilterMemberId] = useState<string | null>(null);
  const [filterMemberQuery, setFilterMemberQuery] = useState('');
  const [filterMemberSelected, setFilterMemberSelected] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));
  const [insightsDateRange, setInsightsDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [amountFilter, setAmountFilter] = useState<AmountFilter>(emptyAmountFilter);
  const [voidedFilter, setVoidedFilter] = useState(VOIDED_HIDE);
  const [sourceFilter, setSourceFilter] = useState(SOURCE_ANY);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordContributionPreferences(church?.id),
  );
  const [formKey, setFormKey] = useState(0);

  const givingSettings = useGivingSettings();

  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;
  const amountBounds = amountFilterBounds(amountFilter);
  const listCurrency = church?.baseCurrency ?? 'GHS';

  const canRecord = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const canVoid = hasPermission(membership, PERMISSIONS.GIVING_VOID);

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string; defaultCurrency: string; isActive: boolean }> }>(
        '/giving/funds?pageSize=100&activeOnly=true',
      ),
  });

  const applyDefaultFund = () => {
    const defaults = resolveDefaultFund(givingSettings.data, 'tithe', funds.data?.data);
    setFundId(defaults.fundId);
    setFundCurrency(defaults.currency);
  };

  const resetRecordForm = () => {
    setFormMemberId(null);
    setFormMemberQuery('');
    setAmountMinor(null);
    setContributionDate(todayIso());
    setMethod('cash');
    setAccountId(null);
    applyDefaultFund();
  };

  useEffect(() => {
    if (!open) return;
    applyDefaultFund();
  }, [open]);

  useEffect(() => {
    if (!open || fundId != null) return;
    if (!funds.data?.data?.length || !givingSettings.data) return;
    applyDefaultFund();
  }, [open, fundId, funds.data?.data, givingSettings.data]);

  const formMembers = useQuery({
    queryKey: ['people', 'members', 'picker', 'tithe-form', formMemberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${formMemberQuery ? `&q=${encodeURIComponent(formMemberQuery)}` : ''}`,
      ),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const filterMembers = useQuery({
    queryKey: ['people', 'members', 'picker', 'tithe-filter', filterMemberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${filterMemberQuery ? `&q=${encodeURIComponent(filterMemberQuery)}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });

  const list = useQuery({
    queryKey: [
      'giving',
      'tithes',
      page,
      pageSize,
      search,
      filterMemberId,
      dateFrom,
      dateTo,
      amountBounds.amountMinMinor,
      amountBounds.amountMaxMinor,
      voidedFilter,
      sourceFilter,
    ],
    queryFn: () =>
      api.get<{
        data: Contribution[];
        page: { totalItems: number; totalPages: number };
        totals?: { amountMinor: number; count: number };
      }>('/giving/contributions', {
        query: {
          page,
          pageSize,
          contributionType: 'tithe',
          q: search.trim() || undefined,
          ...(filterMemberId ? { memberId: filterMemberId } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          ...amountBounds,
          includeVoided: voidedFilter === VOIDED_INCLUDE ? true : undefined,
          ...(sourceFilter === SOURCE_ONLINE
            ? { recordSource: 'online' }
            : sourceFilter === SOURCE_MANUAL
              ? { recordSource: 'manual' }
              : {}),
        },
      }),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: ['giving', 'insights', 'tithe', insightsDateRange.from, insightsDateRange.to],
    queryFn: () =>
      api.get<TitheInsights>('/giving/insights', {
        query: {
          contributionType: 'tithe',
          dateFrom: insightsDateRange.from ?? undefined,
          dateTo: insightsDateRange.to ?? undefined,
        },
      }),
  });

  const currency = fundCurrency ?? church?.baseCurrency ?? 'GHS';
  const fundNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const fund of funds.data?.data ?? []) map.set(fund.id, fund.name);
    return map;
  }, [funds.data?.data]);

  const formMemberOptions = useMemo(
    () =>
      (formMembers.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [formMembers.data?.data],
  );
  const selectedFormMember =
    formMemberOptions.find((option) => option.id === formMemberId) ?? null;

  const filterMemberOptions = useMemo(
    () =>
      (filterMembers.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [filterMembers.data?.data],
  );
  const selectedFilterMember =
    filterMemberOptions.find((option) => option.id === filterMemberId) ??
    filterMemberSelected;

  const dateFilterApplied =
    dateRange.preset !== 'allTime' || Boolean(dateRange.from || dateRange.to);
  const appliedCount =
    (search.trim() ? 1 : 0) +
    (filterMemberId ? 1 : 0) +
    (dateFilterApplied ? 1 : 0) +
    (isAmountFilterApplied(amountFilter) ? 1 : 0) +
    (voidedFilter === VOIDED_INCLUDE ? 1 : 0) +
    (sourceFilter !== SOURCE_ANY ? 1 : 0);

  const record = useMutation({
    mutationFn: () =>
      api.post<Contribution>('/giving/contributions', {
        fundId,
        memberId: formMemberId,
        amountMinor,
        currency,
        contributionDate,
        method,
        financialPositionItemId: accountKindForMethod(method) ? accountId : null,
        contributionType: 'tithe',
      }),
    onSuccess: () => {
      toast.success(t('tithes.saved'));
      void queryClient.invalidateQueries({ queryKey: ['giving', 'tithes'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'insights'] });
      if (savePreferences.addAnother) {
        resetRecordForm();
        setFormKey((current) => current + 1);
        return;
      }
      setOpen(false);
      setFundId(null);
      setFundCurrency(null);
      setFormMemberId(null);
      setAmountMinor(null);
      setContributionDate(todayIso());
      setMethod('cash');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const voidContribution = useMutation({
    mutationFn: () =>
      api.post(`/giving/contributions/${voidTarget!.id}/void`, {
        reason: voidReason,
        password: voidPassword,
      }),
    onSuccess: () => {
      toast.success(t('contributions.voided'));
      setVoidTarget(null);
      setVoidReason('');
      setVoidPassword('');
      void queryClient.invalidateQueries({ queryKey: ['giving', 'tithes'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'insights'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Contribution>[] = [
    {
      id: 'date',
      header: t('contributions.columns.date'),
      cell: (row) => formatDate(row.contributionDate),
    },
    {
      id: 'giver',
      header: t('tithes.columns.giver'),
      cell: (row) =>
        row.identityHidden ? (
          t('contributions.identityHidden')
        ) : (
          <MemberNameLink memberId={row.memberId} name={row.giverName ?? ''} />
        ),
    },
    {
      id: 'amount',
      header: t('contributions.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'fund',
      header: t('tithes.columns.fund'),
      cell: (row) => fundNameById.get(row.fundId) ?? '—',
    },
    {
      id: 'method',
      header: t('contributions.columns.method'),
      cell: (row) =>
        row.isOnline ? t('methods.online') : t(`methods.${row.method}` as 'methods.cash'),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('contributions.columns.status'),
      cell: (row) =>
        row.isVoided ? (
          <Badge variant="danger">{t('contributions.voided')}</Badge>
        ) : row.identityHidden ? (
          <Badge variant="neutral">{t('contributions.identityHidden')}</Badge>
        ) : (
          '—'
        ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const totalsCurrency = church?.baseCurrency ?? 'GHS';

  return (
    <div>
      <PageHeader
        title={t('tithes.title')}
        description={t('tithes.description')}
        headerActions={
          canRecord ? (
            <Button
              onClick={() => {
                setContributionDate(todayIso());
                setOpen(true);
              }}
            >
              {t('tithes.record')}
            </Button>
          ) : undefined
        }
      />
      <GivingSubnav />

      <CollapsibleInsights
        summary={[
          {
            label: t('tithes.insights.total'),
            value: formatInsightsMoneySummary(insights.data, church?.baseCurrency ?? 'GHS'),
          },
          {
            label: t('tithes.insights.gifts'),
            value: insights.data ? formatNumber(insights.data.count) : '…',
          },
        ]}
      >
        <DateRangeFilter
          value={insightsDateRange}
          onChange={setInsightsDateRange}
          labels={{
            today: tCommon('date.today'),
            yesterday: tCommon('date.yesterday'),
            thisWeek: tCommon('date.thisWeek'),
            lastWeek: tCommon('date.lastWeek'),
            thisMonth: tCommon('date.thisMonth'),
            lastMonth: tCommon('date.lastMonth'),
            thisQuarter: tCommon('date.thisQuarter'),
            thisYear: tCommon('date.thisYear'),
            lastYear: tCommon('date.lastYear'),
            allTime: tCommon('date.allTime'),
            custom: tCommon('date.custom'),
            from: t('form.dateFrom'),
            to: t('form.dateTo'),
          }}
          presets={INSIGHTS_DATE_RANGE_PRESETS}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {insightsHasMultipleCurrencies(insights.data) ? (
            insights.data!.byCurrency!.map((item) => (
              <StatCard
                key={item.currency}
                label={t('tithes.insights.total')}
                money={{ amount: item.amountMinor, currency: item.currency }}
                icon={<HandCoins className="size-4" aria-hidden />}
                loading={insights.isLoading}
              />
            ))
          ) : (
            <StatCard
              label={t('tithes.insights.total')}
              money={
                insights.data
                  ? { amount: insights.data.amountMinor, currency: insights.data.currency }
                  : undefined
              }
              icon={<HandCoins className="size-4" aria-hidden />}
              loading={insights.isLoading}
              comparison={
                insights.data
                  ? {
                      change: insights.data.comparison.change,
                      label: t('tithes.insights.vsPreviousPeriod'),
                      formatted: (() => {
                        const change = insights.data.comparison.change;
                        const formatted = formatPercent(Math.abs(change));
                        if (change > 0) return `+${formatted}`;
                        if (change < 0) return `-${formatted}`;
                        return formatted;
                      })(),
                    }
                  : undefined
              }
            />
          )}
          <StatCard
            label={t('tithes.insights.gifts')}
            value={insights.data ? formatNumber(insights.data.count) : undefined}
            icon={<Gift className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <GivingTrendChart
            title={t('tithes.insights.trend')}
            emptyTitle={t('contributions.insights.trendEmpty')}
            dateFrom={insightsDateRange.from}
            dateTo={insightsDateRange.to}
            baseCurrency={church?.baseCurrency ?? 'GHS'}
            contributionType="tithe"
            fallbackPoints={(insights.data?.trend ?? []).map((point) => ({
              label: point.label,
              value: point.amountMinor,
              grain: point.grain,
            }))}
            fallbackLoading={insights.isLoading}
            height={200}
          />
          {(insights.data?.byFund.length ?? 0) > 0 ? (
            <ChartCard title={t('tithes.insights.byFund')} height={200}>
              <SimpleBarChart
                orientation="horizontal"
                data={(insights.data?.byFund ?? []).map((row) => ({
                  label: row.fundName,
                  value: row.amountMinor,
                  currency: row.currency,
                }))}
                formatValue={(value, item) =>
                  formatMoney(value, item?.currency ?? church?.baseCurrency ?? 'GHS', {
                    showSymbol: true,
                  })
                }
                height={180}
              />
            </ChartCard>
          ) : null}
        </div>
      </CollapsibleInsights>

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setFilterMemberId(null);
          setFilterMemberSelected(null);
          setFilterMemberQuery('');
          setDateRange({
            preset: 'allTime',
            ...resolvePreset('allTime', timezone),
          });
          setAmountFilter(emptyAmountFilter());
          setVoidedFilter(VOIDED_HIDE);
          setSourceFilter(SOURCE_ANY);
          setPage(1);
        }}
        labels={{
          filters: t('tithes.filters.title'),
          clearAll: t('tithes.filters.clearAll'),
          applied: (count) => t('tithes.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('tithes.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('tithes.filters.search')}
          />
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-56 min-w-[12rem]">
              <EntityCombobox
                value={filterMemberId}
                onChange={(next) => {
                  setFilterMemberId(next);
                  if (!next) {
                    setFilterMemberSelected(null);
                  } else {
                    const option =
                      filterMemberOptions.find((item) => item.id === next) ?? null;
                    setFilterMemberSelected(option);
                  }
                  setPage(1);
                }}
                options={filterMemberOptions}
                selected={selectedFilterMember}
                onQueryChange={setFilterMemberQuery}
                loading={filterMembers.isPending && !filterMembers.data}
                placeholder={t('tithes.filters.memberPlaceholder')}
                clearLabel={tCommon('action.clearSelection')}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                setPage(1);
              }}
              popoverPortal={popoverPortal}
              labels={{
                today: tCommon('date.today'),
                yesterday: tCommon('date.yesterday'),
                thisWeek: tCommon('date.thisWeek'),
                lastWeek: tCommon('date.lastWeek'),
                thisMonth: tCommon('date.thisMonth'),
                lastMonth: tCommon('date.lastMonth'),
                thisQuarter: tCommon('date.thisQuarter'),
                thisYear: tCommon('date.thisYear'),
                lastYear: tCommon('date.lastYear'),
                allTime: tCommon('date.allTime'),
                custom: tCommon('date.custom'),
                from: t('form.dateFrom'),
                to: t('form.dateTo'),
              }}
            />
            <AmountRangeFilter
              value={amountFilter}
              onChange={(next) => {
                setAmountFilter(next);
                setPage(1);
              }}
              currency={listCurrency}
              popoverPortal={popoverPortal}
              labels={{
                any: t('tithes.filters.amountAny'),
                exact: t('tithes.filters.amountExact'),
                range: t('tithes.filters.amountRange'),
                amount: t('form.amount'),
                from: t('tithes.filters.amountFrom'),
                to: t('tithes.filters.amountTo'),
              }}
            />
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={sourceFilter}
                onChange={(value) => {
                  setSourceFilter(value ?? SOURCE_ANY);
                  setPage(1);
                }}
                options={[
                  { value: SOURCE_ANY, label: t('tithes.filters.sourceAny') },
                  { value: SOURCE_ONLINE, label: t('tithes.filters.sourceOnline') },
                  { value: SOURCE_MANUAL, label: t('tithes.filters.sourceManual') },
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={voidedFilter}
                onChange={(value) => {
                  setVoidedFilter(value ?? VOIDED_HIDE);
                  setPage(1);
                }}
                options={[
                  {
                    value: VOIDED_INCLUDE,
                    label: t('tithes.filters.includeVoided'),
                  },
                  {
                    value: VOIDED_HIDE,
                    label: t('tithes.filters.hideVoided'),
                  },
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
          </>
        )}
      </FilterBar>

      <Tip tipId="giving.tithe.privacy" className="mb-4" />

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => setDetailContributionId(row.id)}
        empty={<p className="text-body-sm text-fg-secondary">{t('tithes.empty')}</p>}
        totals={dataTableMoneyColumnTotals('amount', list.data?.totals, totalsCurrency)}
        totalsLabel={t('tithes.totals')}
        rowActions={(row) => {
          if (row.isVoided) {
            return (
              <Button size="sm" variant="outline" onClick={() => setReasonTarget(row)}>
                {t('contributions.viewReason')}
              </Button>
            );
          }
          if (!canVoid || !row.canVoid) return null;
          return (
            <Button size="sm" variant="outline" onClick={() => setVoidTarget(row)}>
              {t('contributions.void')}
            </Button>
          );
        }}
        actionsHeader={tCommon('table.actions')}
      />

      {pageMeta ? (
        <Pagination
          className="mt-4"
          page={page}
          pageSize={pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
          disabled={list.isFetching}
        />
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setFundId(null);
            setFundCurrency(null);
            setFormMemberId(null);
            setFormMemberQuery('');
            setAmountMinor(null);
            setMethod('cash');
          }
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <form
            key={formKey}
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              if (!formMemberId || !fundId || amountMinor == null || !contributionDate) return;
              record.mutate();
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>{t('tithes.record')}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.member')}</Label>
                <EntityCombobox
                  value={formMemberId}
                  onChange={setFormMemberId}
                  options={formMemberOptions}
                  selected={selectedFormMember}
                  onQueryChange={setFormMemberQuery}
                  loading={formMembers.isPending && !formMembers.data}
                  popoverPortal="dialog"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.amount')}</Label>
                <MoneyInput value={amountMinor} onChange={setAmountMinor} currency={currency} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.date')}</Label>
                <DatePicker
                  value={contributionDate}
                  onChange={setContributionDate}
                  popoverPortal="dialog"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('form.method')}</Label>
                <Listbox
                  value={method}
                  onChange={(value) => {
                    setMethod((value as typeof method) ?? 'cash');
                    setAccountId(null);
                  }}
                  options={CONTRIBUTION_METHODS.map((value) => ({
                    value,
                    label: t(`methods.${value}`),
                  }))}
                  popoverPortal="dialog"
                />
              </div>
              <ContributionAccountField
                open={open}
                method={method}
                currency={currency}
                value={accountId}
                onChange={setAccountId}
              />
              <div className="flex flex-col gap-1.5">
                <Label>{t('tithes.fundLabel')}</Label>
                <Tip tipId="giving.tithe.fund" />
                <FundSelect
                  value={fundId}
                  onChange={(next, fund) => {
                    setFundId(next);
                    setFundCurrency(fund?.defaultCurrency ?? null);
                  }}
                  popoverPortal="dialog"
                />
              </div>
              <RecordContributionSaveOptions
                addAnother={savePreferences.addAnother}
                onAddAnotherChange={(addAnother) => {
                  const next = { ...savePreferences, addAnother };
                  setSavePreferences(next);
                  persistRecordContributionPreferences(church?.id, next);
                }}
                disabled={record.isPending}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={record.isPending}
                  disabled={
                    !formMemberId ||
                    !fundId ||
                    amountMinor == null ||
                    !contributionDate ||
                    Boolean(accountKindForMethod(method) && !accountId)
                  }
                >
                  {t('tithes.save')}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>

      <ContributionDetailDialog
        contributionId={detailContributionId}
        open={Boolean(detailContributionId)}
        onOpenChange={(open) => {
          if (!open) setDetailContributionId(null);
        }}
      />

      <Dialog
        open={voidTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setVoidTarget(null);
            setVoidReason('');
            setVoidPassword('');
          }
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('contributions.voidTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!voidReason.trim() || !voidPassword) return;
              voidContribution.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('contributions.voidReason')}</Label>
              <Textarea
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
                rows={3}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tithe-void-password">{t('contributions.voidPassword')}</Label>
              <Input
                id="tithe-void-password"
                type="password"
                value={voidPassword}
                onChange={(event) => setVoidPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <p className="text-caption text-fg-secondary">{t('contributions.voidPasswordHint')}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setVoidTarget(null);
                  setVoidReason('');
                  setVoidPassword('');
                }}
              >
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                loading={voidContribution.isPending}
                disabled={!voidReason.trim() || !voidPassword}
              >
                {t('contributions.voidConfirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <VoidReasonDialog
        open={Boolean(reasonTarget)}
        onClose={() => setReasonTarget(null)}
        reason={reasonTarget?.voidReason}
        voidedByName={reasonTarget?.voidedByName}
      />
    </div>
  );
};
